/**
 * Inline `<svg>` for `html()`: shapes read from the markup and turned into flat triangle meshes, one piece per colour. Pure geometry:
 * no scene, no layout. The compiler sizes the box and the block tools bake the pieces into a GLB (`html-svg-glb.ts`).
 *
 * Read: `path` (M L H V C S Q T A Z, absolute and relative), `line`, `polyline`, `polygon`, `rect` (rx, ry), `circle`, `ellipse`, `g`
 * with `transform`, fill / stroke / stroke-width / linecap / linejoin / miterlimit / fill-rule / opacity from attributes, the `style`
 * attribute and the page's stylesheet rules, and `currentColor`. Not drawn (and reported): filters, masks, clip paths, gradients (drawn
 * as their first stop), patterns, `<use>`, text, images, dashes and markers.
 *
 * Coordinates of the finished mesh: the svg's box is one unit high, x runs -aspect/2..aspect/2, y is up, the face looks down +z.
 */
import { parseColor, toPx, type Rgba } from './html-style';

/** Where a baked svg is registered in the scene's assets. */
export const SVG_GLB_PREFIX = '/generated/svg-';
/** The depth of an svg's box, in heights of the svg: the shapes are a hair of relief on its front. */
export const SVG_RELIEF = 0.04;

export interface SvgNode { tag: string; attrs: Record<string, string>; kids: (SvgNode | string)[] }

/**
 * One colour of one svg: the triangles drawn in it. `color` is null for `currentColor`; `alpha` is the opacity a `currentColor` piece is
 * drawn at, over the ink's own (an explicit colour carries it in `color.a`).
 */
export interface SvgPiece { color: Rgba | null; alpha: number; positions: number[]; indices: number[] }
export interface SvgPlan {
    /** The box in CSS px. */
    width: number;
    height: number;
    pieces: SvgPiece[];
}

export interface SvgInput {
    node: SvgNode;
    /** Declarations the page's stylesheet gives a node (class, id and tag rules), lowercase. */
    sheetOf(node: SvgNode): Record<string, string>;
    /** Font size the svg's em units are against. */
    fontPx: number;
    warn: (message: string) => void;
}

type Mat = [number, number, number, number, number, number];
const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];
const mul = (m: Mat, n: Mat): Mat => [
    m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
];
type P = [number, number];
const apply = (m: Mat, x: number, y: number): P => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
const scaleOf = (m: Mat) => Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));

const nums = (s: string): number[] => (s.match(/[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi) ?? []).map(Number);

/** `transform="translate(..) rotate(..) ..."` as one matrix; null when a function is not understood. */
export function parseSvgTransform(value: string | undefined): Mat | null {
    if (!value || !value.trim()) return IDENTITY;
    let m: Mat = IDENTITY;
    const re = /([a-z]+)\s*\(([^)]*)\)/gi;
    let seen = 0;
    for (let t = re.exec(value); t; t = re.exec(value)) {
        seen++;
        const a = nums(t[2]);
        const rad = (d: number) => d * Math.PI / 180;
        let n: Mat;
        switch (t[1].toLowerCase()) {
            case 'translate': n = [1, 0, 0, 1, a[0] ?? 0, a[1] ?? 0]; break;
            case 'scale': n = [a[0] ?? 1, 0, 0, a[1] ?? a[0] ?? 1, 0, 0]; break;
            case 'rotate': {
                const c = Math.cos(rad(a[0] ?? 0)); const s = Math.sin(rad(a[0] ?? 0));
                const r: Mat = [c, s, -s, c, 0, 0];
                n = a.length >= 3 ? mul(mul([1, 0, 0, 1, a[1], a[2]], r), [1, 0, 0, 1, -a[1], -a[2]]) : r;
                break;
            }
            case 'matrix': if (a.length !== 6) return null; n = a as Mat; break;
            case 'skewx': n = [1, 0, Math.tan(rad(a[0] ?? 0)), 1, 0, 0]; break;
            case 'skewy': n = [1, Math.tan(rad(a[0] ?? 0)), 0, 1, 0, 0]; break;
            default: return null;
        }
        m = mul(m, n);
    }
    return seen ? m : null;
}

// ── Curves ──────────────────────────────────────────────

interface Sub { pts: P[]; closed: boolean }

/** A cubic flattened by recursive subdivision until its control points sit within `tol` of the chord. */
function flattenCubic(out: P[], p0: P, p1: P, p2: P, p3: P, tol: number, depth = 0): void {
    const dx = p3[0] - p0[0]; const dy = p3[1] - p0[1];
    const len = Math.hypot(dx, dy);
    const d1 = len > 1e-9 ? Math.abs((p1[0] - p0[0]) * dy - (p1[1] - p0[1]) * dx) / len : Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
    const d2 = len > 1e-9 ? Math.abs((p2[0] - p0[0]) * dy - (p2[1] - p0[1]) * dx) / len : Math.hypot(p2[0] - p0[0], p2[1] - p0[1]);
    if (depth >= 10 || Math.max(d1, d2) <= tol) { out.push(p3); return; }
    const mid = (a: P, b: P): P => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const a = mid(p0, p1); const b = mid(p1, p2); const c = mid(p2, p3);
    const ab = mid(a, b); const bc = mid(b, c); const m = mid(ab, bc);
    flattenCubic(out, p0, a, ab, m, tol, depth + 1);
    flattenCubic(out, m, bc, c, p3, tol, depth + 1);
}

/** An SVG elliptical arc as cubic segments (each at most a quarter turn): the endpoint-to-centre conversion of the SVG spec. */
function arcToCubics(x1: number, y1: number, rxIn: number, ryIn: number, rotDeg: number, large: boolean, sweep: boolean, x2: number, y2: number): [P, P, P][] {
    let rx = Math.abs(rxIn); let ry = Math.abs(ryIn);
    if ((x1 === x2 && y1 === y2) || rx === 0 || ry === 0) return [[[x1, y1], [x2, y2], [x2, y2]]];
    const phi = rotDeg * Math.PI / 180; const cp = Math.cos(phi); const sp = Math.sin(phi);
    const dx = (x1 - x2) / 2; const dy = (y1 - y2) / 2;
    const x1p = cp * dx + sp * dy; const y1p = -sp * dx + cp * dy;
    const lam = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
    if (lam > 1) { const s = Math.sqrt(lam); rx *= s; ry *= s; }
    const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
    const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
    const co = (large === sweep ? -1 : 1) * Math.sqrt(Math.max(0, num / den));
    const cxp = co * rx * y1p / ry; const cyp = -co * ry * x1p / rx;
    const cx = cp * cxp - sp * cyp + (x1 + x2) / 2; const cy = sp * cxp + cp * cyp + (y1 + y2) / 2;
    const ang = (ux: number, uy: number, vx: number, vy: number) => Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    const th1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
    let dth = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
    if (!sweep && dth > 0) dth -= 2 * Math.PI;
    else if (sweep && dth < 0) dth += 2 * Math.PI;
    const n = Math.max(1, Math.ceil(Math.abs(dth) / (Math.PI / 2) - 1e-9));
    const step = dth / n;
    const k = 4 / 3 * Math.tan(step / 4);
    const out: [P, P, P][] = [];
    const at = (t: number): P => [cp * rx * Math.cos(t) - sp * ry * Math.sin(t) + cx, sp * rx * Math.cos(t) + cp * ry * Math.sin(t) + cy];
    const dAt = (t: number): P => [-cp * rx * Math.sin(t) - sp * ry * Math.cos(t), -sp * rx * Math.sin(t) + cp * ry * Math.cos(t)];
    for (let i = 0; i < n; i++) {
        const a = th1 + i * step; const b = a + step;
        const pa = at(a); const pb = at(b); const da = dAt(a); const db = dAt(b);
        out.push([[pa[0] + k * da[0], pa[1] + k * da[1]], [pb[0] - k * db[0], pb[1] - k * db[1]], pb]);
    }
    out[out.length - 1][2] = [x2, y2];
    return out;
}

/** Path data as subpaths of points in user space, curves flattened at `tol` (in user units). */
export function flattenPath(d: string, tol: number): Sub[] | null {
    const tokens = d.match(/[a-z]|[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi);
    if (!tokens) return [];
    const subs: Sub[] = [];
    let cur = null as Sub | null;
    let x = 0; let y = 0; let sx = 0; let sy = 0;
    let lastC: P | null = null; let lastQ: P | null = null;
    let i = 0; let cmd = '';
    const num = (): number | null => { const t = tokens[i]; if (t === undefined || /^[a-z]$/i.test(t)) return null; i++; return Number(t); };
    const flag = (): boolean | null => {
        // A flag is one character, and minified paths run it into what follows ("a1 1 0 011.5 1.5"): the rest stays a token.
        const t = tokens[i];
        if (t === undefined || !/^[01]/.test(t)) return null;
        if (t.length > 1) tokens[i] = t.slice(1); else i++;
        return t[0] === '1';
    };
    const start = (px: number, py: number) => { cur = { pts: [[px, py]], closed: false }; subs.push(cur); };
    const ensure = () => { if (!cur) start(x, y); };
    while (i < tokens.length) {
        if (/^[a-z]$/i.test(tokens[i])) { cmd = tokens[i]; i++; if (cmd === 'z' || cmd === 'Z') { if (cur) { cur.closed = true; x = sx; y = sy; cur = null; } lastC = lastQ = null; continue; } }
        else if (cmd === '') return null;
        else if (cmd === 'M') cmd = 'L'; else if (cmd === 'm') cmd = 'l';
        const rel = cmd === cmd.toLowerCase(); const ox = rel ? x : 0; const oy = rel ? y : 0;
        const c = cmd.toUpperCase();
        const get = (n: number): number[] | null => { const v: number[] = []; for (let k = 0; k < n; k++) { const t = num(); if (t === null) return null; v.push(t); } return v; };
        if (c === 'M') { const v = get(2); if (!v) return null; x = v[0] + ox; y = v[1] + oy; sx = x; sy = y; start(x, y); lastC = lastQ = null; }
        else if (c === 'L') { const v = get(2); if (!v) return null; ensure(); x = v[0] + ox; y = v[1] + oy; cur!.pts.push([x, y]); lastC = lastQ = null; }
        else if (c === 'H') { const v = get(1); if (!v) return null; ensure(); x = v[0] + ox; cur!.pts.push([x, y]); lastC = lastQ = null; }
        else if (c === 'V') { const v = get(1); if (!v) return null; ensure(); y = v[0] + oy; cur!.pts.push([x, y]); lastC = lastQ = null; }
        else if (c === 'C' || c === 'S') {
            const v = get(c === 'C' ? 6 : 4); if (!v) return null; ensure();
            const p0: P = [x, y];
            const p1: P = c === 'C' ? [v[0] + ox, v[1] + oy] : lastC ? [2 * x - lastC[0], 2 * y - lastC[1]] : [x, y];
            const o = c === 'C' ? 2 : 0;
            const p2: P = [v[o] + ox, v[o + 1] + oy]; const p3: P = [v[o + 2] + ox, v[o + 3] + oy];
            flattenCubic(cur!.pts, p0, p1, p2, p3, tol);
            x = p3[0]; y = p3[1]; lastC = p2; lastQ = null;
        } else if (c === 'Q' || c === 'T') {
            const v = get(c === 'Q' ? 4 : 2); if (!v) return null; ensure();
            const q: P = c === 'Q' ? [v[0] + ox, v[1] + oy] : lastQ ? [2 * x - lastQ[0], 2 * y - lastQ[1]] : [x, y];
            const o = c === 'Q' ? 2 : 0;
            const e: P = [v[o] + ox, v[o + 1] + oy];
            flattenCubic(cur!.pts, [x, y], [x + 2 / 3 * (q[0] - x), y + 2 / 3 * (q[1] - y)], [e[0] + 2 / 3 * (q[0] - e[0]), e[1] + 2 / 3 * (q[1] - e[1])], e, tol);
            x = e[0]; y = e[1]; lastQ = q; lastC = null;
        } else if (c === 'A') {
            const r = get(3); if (!r) return null;
            const large = flag(); const sweep = flag(); if (large === null || sweep === null) return null;
            const e = get(2); if (!e) return null; ensure();
            const ex = e[0] + ox; const ey = e[1] + oy;
            for (const [a, b, p] of arcToCubics(x, y, r[0], r[1], r[2], large, sweep, ex, ey)) { flattenCubic(cur!.pts, [x, y], a, b, p, tol); x = p[0]; y = p[1]; }
            x = ex; y = ey; lastC = lastQ = null;
        } else return null;
    }
    return subs;
}

// ── Fill ────────────────────────────────────────────────

const area2 = (r: P[]): number => { let s = 0; for (let i = 0, j = r.length - 1; i < r.length; j = i++) s += r[j][0] * r[i][1] - r[i][0] * r[j][1]; return s; };
function inside(pt: P, r: P[]): boolean {
    let c = false;
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
        const a = r[i]; const b = r[j];
        if ((a[1] > pt[1]) !== (b[1] > pt[1]) && pt[0] < (b[0] - a[0]) * (pt[1] - a[1]) / (b[1] - a[1]) + a[0]) c = !c;
    }
    return c;
}
/** A point on or beside ring `r` that is inside it, for testing containment by another ring: its first vertex nudged toward the centroid. */
function probe(r: P[]): P {
    let cx = 0; let cy = 0; for (const p of r) { cx += p[0]; cy += p[1]; }
    cx /= r.length; cy /= r.length;
    return [r[0][0] + (cx - r[0][0]) * 1e-3, r[0][1] + (cy - r[0][1]) * 1e-3];
}

/**
 * Triangles for a set of closed rings under a fill rule. Rings nest: a ring's region is what is inside it and outside its children, and
 * is filled when the winding of every ring around it adds to non-zero (nonzero) or its depth is even (evenodd). Holes are the unfilled
 * children of a filled ring.
 */
export function fillRings(rings: P[][], rule: 'nonzero' | 'evenodd', earcut: (data: number[], holes: number[]) => number[]): { positions: number[]; indices: number[] } {
    const rs = rings.map(r => { const o = r.slice(); if (o.length > 1 && o[0][0] === o[o.length - 1][0] && o[0][1] === o[o.length - 1][1]) o.pop(); return o; })
        .filter(r => r.length >= 3 && Math.abs(area2(r)) > 1e-9);
    const parent: number[] = rs.map(() => -1);
    const sizes = rs.map(r => Math.abs(area2(r)));
    const probes = rs.map(probe);
    for (let i = 0; i < rs.length; i++) {
        let best = -1;
        for (let j = 0; j < rs.length; j++) {
            if (i === j || sizes[j] <= sizes[i] || !inside(probes[i], rs[j])) continue;
            if (best < 0 || sizes[j] < sizes[best]) best = j;
        }
        parent[i] = best;
    }
    const wind: number[] = []; const depth: number[] = [];
    const order = rs.map((_, i) => i).sort((a, b) => sizes[b] - sizes[a]);
    for (const i of order) {
        const p = parent[i];
        wind[i] = (p < 0 ? 0 : wind[p]) + (area2(rs[i]) > 0 ? 1 : -1);
        depth[i] = p < 0 ? 0 : depth[p] + 1;
    }
    const filled = (i: number) => rule === 'evenodd' ? depth[i] % 2 === 0 : wind[i] !== 0;
    const positions: number[] = []; const indices: number[] = [];
    for (let i = 0; i < rs.length; i++) {
        if (!filled(i)) continue;
        const holes = rs.map((_, j) => j).filter(j => parent[j] === i && !filled(j));
        const data: number[] = []; const holeAt: number[] = [];
        for (const p of rs[i]) data.push(p[0], p[1]);
        for (const h of holes) { holeAt.push(data.length / 2); for (const p of rs[h]) data.push(p[0], p[1]); }
        const base = positions.length / 3;
        for (let k = 0; k < data.length; k += 2) positions.push(data[k], data[k + 1], 0);
        for (const t of earcut(data, holeAt)) indices.push(base + t);
    }
    return { positions, indices };
}

// ── Stroke ──────────────────────────────────────────────

export interface StrokeStyle { width: number; cap: 'butt' | 'round' | 'square'; join: 'miter' | 'round' | 'bevel'; miterLimit: number }

/** A polyline as a band of triangles `width` wide, with the join at each corner and the cap at each end. Pieces overlap; that is fine. */
export function strokeSub(sub: Sub, st: StrokeStyle): { positions: number[]; indices: number[] } {
    const positions: number[] = []; const indices: number[] = [];
    const hw = st.width / 2;
    const vert = (x: number, y: number) => { positions.push(x, y, 0); return positions.length / 3 - 1; };
    const tri = (a: P, b: P, c: P) => {
        const i = vert(a[0], a[1]); const j = vert(b[0], b[1]); const k = vert(c[0], c[1]);
        const cr = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
        if (Math.abs(cr) < 1e-12) return;
        if (cr > 0) indices.push(i, j, k); else indices.push(i, k, j);
    };
    const pts: P[] = [];
    for (const p of sub.pts) { const l = pts[pts.length - 1]; if (!l || Math.hypot(p[0] - l[0], p[1] - l[1]) > 1e-6) pts.push(p); }
    let closed = sub.closed;
    if (closed && pts.length > 1 && Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]) <= 1e-6) pts.pop();
    if (pts.length < 2) {
        if (pts.length === 1 && st.cap === 'round') disc(pts[0], hw, tri);
        else if (pts.length === 1 && st.cap === 'square') {
            tri([pts[0][0] - hw, pts[0][1] - hw], [pts[0][0] + hw, pts[0][1] - hw], [pts[0][0] + hw, pts[0][1] + hw]);
            tri([pts[0][0] - hw, pts[0][1] - hw], [pts[0][0] + hw, pts[0][1] + hw], [pts[0][0] - hw, pts[0][1] + hw]);
        }
        return { positions, indices };
    }
    if (pts.length === 2) closed = false;
    const n = pts.length;
    const segs = closed ? n : n - 1;
    const dir = (i: number): P => { const a = pts[i]; const b = pts[(i + 1) % n]; const l = Math.hypot(b[0] - a[0], b[1] - a[1]); return [(b[0] - a[0]) / l, (b[1] - a[1]) / l]; };
    const ds: P[] = []; for (let i = 0; i < segs; i++) ds.push(dir(i));
    for (let i = 0; i < segs; i++) {
        const a = pts[i]; const b = pts[(i + 1) % n]; const d = ds[i]; const nx = -d[1] * hw; const ny = d[0] * hw;
        let a0: P = a; let b0: P = b;
        if (!closed && st.cap === 'square') { if (i === 0) a0 = [a[0] - d[0] * hw, a[1] - d[1] * hw]; if (i === segs - 1) b0 = [b[0] + d[0] * hw, b[1] + d[1] * hw]; }
        tri([a0[0] + nx, a0[1] + ny], [a0[0] - nx, a0[1] - ny], [b0[0] - nx, b0[1] - ny]);
        tri([a0[0] + nx, a0[1] + ny], [b0[0] - nx, b0[1] - ny], [b0[0] + nx, b0[1] + ny]);
    }
    const joins = closed ? n : n - 2;
    for (let k = 0; k < joins; k++) {
        const vi = closed ? k : k + 1;
        const d1 = ds[(vi - 1 + segs) % segs]; const d2 = ds[vi % segs];
        const p = pts[vi];
        const cr = d1[0] * d2[1] - d1[1] * d2[0]; const dot = d1[0] * d2[0] + d1[1] * d2[1];
        if (Math.abs(cr) < 1e-6 && dot > 0) continue;
        const s = cr > 0 ? -1 : 1;
        const n1: P = [-d1[1] * s, d1[0] * s]; const n2: P = [-d2[1] * s, d2[0] * s];
        const o1: P = [p[0] + n1[0] * hw, p[1] + n1[1] * hw]; const o2: P = [p[0] + n2[0] * hw, p[1] + n2[1] * hw];
        if (st.join === 'round') {
            const a1 = Math.atan2(n1[1], n1[0]); let da = Math.atan2(n2[1], n2[0]) - a1;
            while (da > Math.PI) da -= 2 * Math.PI; while (da < -Math.PI) da += 2 * Math.PI;
            const steps = Math.max(1, Math.ceil(Math.abs(da) / (Math.PI / 6)));
            let prev = o1;
            for (let t = 1; t <= steps; t++) { const a = a1 + da * t / steps; const q: P = [p[0] + Math.cos(a) * hw, p[1] + Math.sin(a) * hw]; tri(p, prev, q); prev = q; }
        } else if (st.join === 'miter') {
            const ratio = 1 / Math.sqrt(Math.max(1e-9, (1 + dot) / 2));
            if (ratio <= st.miterLimit) {
                const mx = n1[0] + n2[0]; const my = n1[1] + n2[1]; const ml = Math.hypot(mx, my);
                const tip: P = [p[0] + mx / ml * hw * ratio, p[1] + my / ml * hw * ratio];
                tri(p, o1, tip); tri(p, tip, o2);
            } else tri(p, o1, o2);
        } else tri(p, o1, o2);
    }
    if (!closed && st.cap === 'round') {
        for (const [p, d] of [[pts[0], [-ds[0][0], -ds[0][1]]], [pts[n - 1], ds[segs - 1]]] as [P, P][]) {
            const a0 = Math.atan2(d[1], d[0]) - Math.PI / 2;
            const steps = 6;
            let prev: P = [p[0] + Math.cos(a0) * hw, p[1] + Math.sin(a0) * hw];
            for (let t = 1; t <= steps; t++) { const a = a0 + Math.PI * t / steps; const q: P = [p[0] + Math.cos(a) * hw, p[1] + Math.sin(a) * hw]; tri(p, prev, q); prev = q; }
        }
    }
    return { positions, indices };
}
function disc(c: P, r: number, tri: (a: P, b: P, c: P) => void): void {
    const steps = 12;
    for (let t = 0; t < steps; t++) {
        const a = 2 * Math.PI * t / steps; const b = 2 * Math.PI * (t + 1) / steps;
        tri(c, [c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r], [c[0] + Math.cos(b) * r, c[1] + Math.sin(b) * r]);
    }
}

// ── The svg element ─────────────────────────────────────

/** `style="a:b;c:d"` as lowercase declarations. */
export function inlineStyle(s: string | undefined): Record<string, string> {
    const out: Record<string, string> = {};
    for (const part of (s ?? '').split(';')) {
        const at = part.indexOf(':');
        if (at > 0) out[part.slice(0, at).trim().toLowerCase()] = part.slice(at + 1).trim().toLowerCase();
    }
    return out;
}

/** A CSS/SVG opacity (a number or a percentage) clamped to 0..1; 1 when it is not one. */
const opacityOf = (v: string | undefined): number => {
    if (v === undefined) return 1;
    const n = v.trim().endsWith('%') ? parseFloat(v) / 100 : parseFloat(v);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 1;
};

/** What an inline svg may hold once flattened. */
const MAX_RINGS = 400;
const MAX_POINTS = 20_000;

const INHERITED = ['fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'fill-rule', 'fill-opacity', 'stroke-opacity', 'color', 'visibility', 'stroke-dasharray'] as const;
type Inherited = Partial<Record<typeof INHERITED[number], string>>;

const SHAPES = new Set(['path', 'line', 'polyline', 'polygon', 'rect', 'circle', 'ellipse']);
const SILENT = new Set(['title', 'desc', 'metadata', 'style', 'defs', 'symbol', 'lineargradient', 'radialgradient', 'stop', 'sodipodi:namedview']);
const UNSUPPORTED: Record<string, string> = {
    use: '<use>', text: 'text', tspan: 'text', image: 'an image', foreignobject: 'foreignObject', filter: 'a filter', mask: 'a mask', clippath: 'a clip path',
    pattern: 'a pattern', marker: 'a marker', animate: 'animation', animatetransform: 'animation', set: 'animation', switch: '<switch>',
};

/** A width or height in px; null when unset, or a percentage (the page's box is not known here, so the viewBox sizes it). */
const lengthIn = (v: string | undefined, fontPx: number): number | null => {
    if (v === undefined) return null;
    const t = v.trim().toLowerCase();
    if (t === '' || t === 'auto' || t.endsWith('%')) return null;
    const px = toPx(t, fontPx);
    return px !== null && px > 0 ? px : null;
};

/** What the shapes read of an `<svg>`'s own style and attributes; the compiler reports the rest as it does for any element. */
export const SVG_PAINT = new Set(['fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'fill-rule', 'fill-opacity',
    'stroke-opacity', 'stroke-dasharray']);
export const SVG_READS = new Set([...SVG_PAINT, 'width', 'height', 'color', 'opacity', 'display', 'visibility', 'transform', 'viewbox', 'preserveaspectratio',
    'xmlns', 'xmlns:xlink', 'version', 'focusable']);

/** An svg element as flat shapes in its box. Returns null when nothing is drawn. */
export function compileSvg(input: SvgInput, earcut: (data: number[], holes: number[]) => number[]): SvgPlan | null {
    const { node, warn } = input;
    const dropped = new Set<string>();
    const drop = (what: string) => { if (!dropped.has(what)) { dropped.add(what); warn(`<svg>: ${what} is not drawn; skipped.`); } };

    // ── Box
    const own = { ...input.sheetOf(node), ...inlineStyle(node.attrs.style) };
    const vb = node.attrs.viewbox ? nums(node.attrs.viewbox) : [];
    const hasVb = vb.length === 4 && vb[2] > 0 && vb[3] > 0;
    let width = lengthIn(own.width ?? node.attrs.width, input.fontPx);
    let height = lengthIn(own.height ?? node.attrs.height, input.fontPx);
    if (hasVb) {
        if (width === null && height === null) { width = vb[2]; height = vb[3]; }
        else if (width === null) width = height! * vb[2] / vb[3];
        else if (height === null) height = width * vb[3] / vb[2];
    }
    const def = !hasVb && (width === null || height === null);

    // ── Walk
    const pieces = new Map<string, SvgPiece>();
    const gradients = new Map<string, string>();
    const collectDefs = (n: SvgNode) => {
        for (const k of n.kids) {
            if (typeof k === 'string') continue;
            if ((k.tag === 'lineargradient' || k.tag === 'radialgradient') && k.attrs.id) {
                const stop = k.kids.find((s): s is SvgNode => typeof s !== 'string' && s.tag === 'stop');
                const col = stop ? (inlineStyle(stop.attrs.style)['stop-color'] ?? stop.attrs['stop-color']) : undefined;
                // Keyed in lower case: paint values reach here lowercased (the style and stylesheet readers fold case), ids keep theirs.
                if (col) gradients.set(k.attrs.id.toLowerCase(), col);
            }
            collectDefs(k);
        }
    };
    collectDefs(node);
    if (own.filter || node.attrs.filter) drop('a filter');
    if (own.mask || node.attrs.mask) drop('a mask');
    if (own['clip-path'] || node.attrs['clip-path']) drop('a clip path');

    // Shapes are collected in user space first, since the box size may come from their bounds.
    const shapes: { subs: Sub[]; m: Mat; style: Inherited; opacity: number }[] = [];

    const walk = (n: SvgNode, m: Mat, inherit: Inherited, opacity: number) => {
        for (const k of n.kids) {
            if (typeof k === 'string') continue;
            if (SILENT.has(k.tag)) continue;
            if (UNSUPPORTED[k.tag]) { drop(UNSUPPORTED[k.tag]); continue; }
            const decl = { ...input.sheetOf(k), ...inlineStyle(k.attrs.style) };
            const pick = (name: string): string | undefined => decl[name] ?? k.attrs[name];
            if (pick('display') === 'none') continue;
            if (k.attrs['clip-path'] || decl['clip-path']) drop('a clip path');
            if (['marker-start', 'marker-mid', 'marker-end', 'marker'].some(a => pick(a) !== undefined && pick(a) !== 'none')) drop('a marker');
            if (k.attrs.mask || decl.mask) drop('a mask');
            if (k.attrs.filter || decl.filter) drop('a filter');
            const mine: Inherited = { ...inherit };
            for (const name of INHERITED) { const v = pick(name); if (v !== undefined && v !== 'inherit') mine[name] = v.trim().toLowerCase(); }
            const nextOpacity = opacity * opacityOf(pick('opacity'));
            const t = parseSvgTransform(pick('transform'));
            if (t === null) { drop('this transform'); continue; }
            const mm = mul(m, t);
            if (k.tag === 'g' || k.tag === 'a' || k.tag === 'svg') {
                if (k.tag === 'svg') drop('a nested <svg>');
                else walk(k, mm, mine, nextOpacity);
                continue;
            }
            if (!SHAPES.has(k.tag)) { drop(`<${k.tag}>`); continue; }
            const subs = shapeToSubs(k, pick, scaleOf(mm));
            if (mine.visibility === 'hidden' || mine.visibility === 'collapse') continue;
            if (subs) shapes.push({ subs, m: mm, style: mine, opacity: nextOpacity });
            else drop(`a malformed <${k.tag}>`);
        }
    };
    const shapeToSubs = (k: SvgNode, pick: (n: string) => string | undefined, scale: number): Sub[] | null => {
        const f = (name: string, dflt = 0) => { const v = pick(name) ?? k.attrs[name]; const n = v === undefined ? NaN : parseFloat(v); return Number.isFinite(n) ? n : dflt; };
        // The tolerance is in this shape's own units: a group that scales it up flattens it finer.
        const toler = tolFor.value / Math.max(scale, 1e-6);
        switch (k.tag) {
            case 'path': return flattenPath(k.attrs.d ?? '', toler);
            case 'line': return [{ pts: [[f('x1'), f('y1')], [f('x2'), f('y2')]], closed: false }];
            case 'polyline': case 'polygon': {
                const v = nums(k.attrs.points ?? '');
                if (v.length < 4) return null;
                const pts: P[] = []; for (let i = 0; i + 1 < v.length; i += 2) pts.push([v[i], v[i + 1]]);
                return [{ pts, closed: k.tag === 'polygon' }];
            }
            case 'rect': {
                const x = f('x'); const y = f('y'); const w = f('width'); const h = f('height');
                if (w <= 0 || h <= 0) return [];
                let rx = pick('rx') !== undefined ? f('rx') : NaN; let ry = pick('ry') !== undefined ? f('ry') : NaN;
                if (Number.isNaN(rx) && Number.isNaN(ry)) { rx = 0; ry = 0; } else if (Number.isNaN(rx)) rx = ry; else if (Number.isNaN(ry)) ry = rx;
                rx = Math.min(Math.max(rx, 0), w / 2); ry = Math.min(Math.max(ry, 0), h / 2);
                if (rx === 0 || ry === 0) return [{ pts: [[x, y], [x + w, y], [x + w, y + h], [x, y + h]], closed: true }];
                const d = `M${x + rx} ${y}H${x + w - rx}A${rx} ${ry} 0 0 1 ${x + w} ${y + ry}V${y + h - ry}A${rx} ${ry} 0 0 1 ${x + w - rx} ${y + h}H${x + rx}A${rx} ${ry} 0 0 1 ${x} ${y + h - ry}V${y + ry}A${rx} ${ry} 0 0 1 ${x + rx} ${y}Z`;
                return flattenPath(d, toler);
            }
            case 'circle': case 'ellipse': {
                const cx = f('cx'); const cy = f('cy');
                const rx = k.tag === 'circle' ? f('r') : f('rx'); const ry = k.tag === 'circle' ? rx : f('ry');
                if (rx <= 0 || ry <= 0) return [];
                return flattenPath(`M${cx + rx} ${cy}A${rx} ${ry} 0 0 1 ${cx} ${cy + ry}A${rx} ${ry} 0 0 1 ${cx - rx} ${cy}A${rx} ${ry} 0 0 1 ${cx} ${cy - ry}A${rx} ${ry} 0 0 1 ${cx + rx} ${cy}Z`, toler);
            }
            default: return null;
        }
    };
    // Flatness tolerance in viewBox units: about 0.04 px of the box when the viewBox and size give the scale before the walk, else 0.04
    // user units.
    const tolFor = { value: 0.04 };
    const guessScale = hasVb && width !== null && height !== null ? Math.min(width / vb[2], height / vb[3]) : 1;
    tolFor.value = 0.04 / Math.max(guessScale, 1e-6);

    const rootDecl = own;
    const rootInherit: Inherited = {};
    for (const name of INHERITED) { const v = rootDecl[name] ?? node.attrs[name]; if (v !== undefined) rootInherit[name] = v.trim().toLowerCase(); }
    const parsedRootT = parseSvgTransform(own.transform ?? node.attrs.transform);
    if (parsedRootT === null) drop('this transform');
    const rootT = parsedRootT ?? IDENTITY;
    walk(node, rootT, rootInherit, opacityOf(rootDecl.opacity ?? node.attrs.opacity));

    if (!shapes.length) { if (!dropped.size) warn('<svg> draws no shapes; skipped.'); return null; }
    // A drawing past these is an illustration, not an icon: its ring nesting is quadratic, so it is left out rather than stall the build.
    const rings = shapes.reduce((n, s) => n + s.subs.length, 0);
    const points = shapes.reduce((n, s) => n + s.subs.reduce((k, sub) => k + sub.pts.length, 0), 0);
    if (rings > MAX_RINGS || points > MAX_POINTS) {
        warn(`<svg> has ${rings} subpaths and ${points} points after flattening, over the ${MAX_RINGS} and ${MAX_POINTS} an inline icon may have; it is not drawn.`);
        return null;
    }

    // ── Box fallback: the drawn bounds
    // With no viewBox, user units are px from the box's corner, so a sized svg is its own viewBox; an unsized one takes its drawn bounds.
    let vbx = hasVb ? vb[0] : 0; let vby = hasVb ? vb[1] : 0; let vbw = hasVb ? vb[2] : def ? 0 : width!; let vbh = hasVb ? vb[3] : def ? 0 : height!;
    if (def) {
        let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
        for (const s of shapes) for (const sub of s.subs) for (const p of sub.pts) { const q = apply(s.m, p[0], p[1]); x0 = Math.min(x0, q[0]); y0 = Math.min(y0, q[1]); x1 = Math.max(x1, q[0]); y1 = Math.max(y1, q[1]); }
        vbx = x0; vby = y0; vbw = Math.max(1e-3, x1 - x0); vbh = Math.max(1e-3, y1 - y0);
        if (width === null && height === null) { width = vbw; height = vbh; } else if (width === null) width = height! * vbw / vbh; else height = width * vbh / vbw;
        warn('<svg> has no viewBox; its drawn bounds were used.');
    }
    const W = width!; const H = height!;
    if (!(W > 0 && H > 0)) return null;

    // viewBox -> box: preserveAspectRatio, default xMidYMid meet.
    const par = (node.attrs.preserveaspectratio ?? 'xmidymid meet').trim().toLowerCase().split(/\s+/);
    let boxM: Mat;
    if (par[0] === 'none') boxM = [W / vbw, 0, 0, H / vbh, -vbx * W / vbw, -vby * H / vbh];
    else {
        const slice = par[1] === 'slice';
        const sc = slice ? Math.max(W / vbw, H / vbh) : Math.min(W / vbw, H / vbh);
        const ax = par[0].includes('xmin') ? 0 : par[0].includes('xmax') ? 1 : 0.5;
        const ay = par[0].includes('ymin') ? 0 : par[0].includes('ymax') ? 1 : 0.5;
        boxM = [sc, 0, 0, sc, (W - vbw * sc) * ax - vbx * sc, (H - vbh * sc) * ay - vby * sc];
    }
    // Box px (y down) to mesh units (height 1, y up, centred).
    const toMesh = (p: P): P => [(p[0] - W / 2) / H, (H / 2 - p[1]) / H];

    const colorOf = (value: string | undefined, current: string | undefined): { color: Rgba | null } | 'none' | null => {
        if (value === undefined) return null;
        const v = value.trim().toLowerCase();
        if (v === 'none' || v === 'transparent') return 'none';
        if (v === 'currentcolor') {
            const c = current ? parseColor(current) : null;
            return c ? { color: c } : { color: null };
        }
        const url = /^url\(\s*['"]?#([^'")\s]+)['"]?\s*\)\s*(.*)$/.exec(v);
        if (url) {
            const stop = gradients.get(url[1].toLowerCase());
            if (stop === undefined) { drop('a pattern or other paint server'); return 'none'; }
            drop('a gradient (drawn as its first colour)');
            const c = parseColor(stop.trim().toLowerCase());
            return c ? { color: c } : 'none';
        }
        const c = parseColor(v);
        if (c) return { color: c };
        drop(`the colour "${v}"`);
        return 'none';
    };
    const add = (color: Rgba | null, alpha: number, g: { positions: number[]; indices: number[] }, mats: Mat) => {
        if (!g.indices.length) return;
        const c = color ? { hex: color.hex, a: Math.round(color.a * alpha * 1e3) / 1e3 } : null;
        const a = Math.round(alpha * 1e3) / 1e3;
        const key = c ? `${c.hex}|${c.a}` : `current|${a}`;
        let piece = pieces.get(key);
        if (!piece) { piece = { color: c, alpha: c ? 1 : a, positions: [], indices: [] }; pieces.set(key, piece); }
        const base = piece.positions.length / 3;
        for (let i = 0; i < g.positions.length; i += 3) {
            const q = toMesh(apply(mats, g.positions[i], g.positions[i + 1]));
            piece.positions.push(Math.round(q[0] * 1e6) / 1e6, Math.round(q[1] * 1e6) / 1e6, 0);
        }
        for (const t of g.indices) piece.indices.push(base + t);
    };

    for (const s of shapes) {
        const full = mul(boxM, s.m);
        const st = s.style;
        const current = st.color ?? rootInherit.color;
        const fillSpec = colorOf(st.fill ?? 'black', current);
        const strokeSpec = colorOf(st.stroke, current);
        const fillA = opacityOf(st['fill-opacity']) * s.opacity;
        const strokeA = opacityOf(st['stroke-opacity']) * s.opacity;
        // Everything in box px (y down): the transform is applied to the points before triangulating.
        const boxSubs: Sub[] = s.subs.map(sub => ({ pts: sub.pts.map(p => apply(full, p[0], p[1])), closed: sub.closed }));
        if (fillSpec !== 'none' && fillSpec !== null && fillA > 0 && !(s.opacity <= 0)) {
            const rule = st['fill-rule'] === 'evenodd' ? 'evenodd' : 'nonzero';
            const rings = boxSubs.filter(sub => sub.pts.length >= 3).map(sub => sub.pts);
            if (rings.length) {
                // Triangulated in box px with y down; `add` flips y, and the winding is put right once all pieces are made (below).
                // Overlapping rings are triangulated each on its own, so where they overlap a translucent fill draws twice (darker).
                const g = fillRings(rings, rule, earcut);
                add(fillSpec.color, fillA, g, IDENTITY);
            }
        }
        if (strokeSpec !== 'none' && strokeSpec !== null && strokeA > 0) {
            const sw = (() => { const v = st['stroke-width']; const n = v === undefined ? 1 : parseFloat(v); return Number.isFinite(n) ? n : 1; })() * scaleOf(full);
            if (st['stroke-dasharray'] && st['stroke-dasharray'] !== 'none' && nums(st['stroke-dasharray']).some(x => x > 0)) drop('a dashed stroke (drawn solid)');
            if (sw > 0) {
                const style: StrokeStyle = {
                    width: sw,
                    cap: st['stroke-linecap'] === 'round' ? 'round' : st['stroke-linecap'] === 'square' ? 'square' : 'butt',
                    join: st['stroke-linejoin'] === 'round' ? 'round' : st['stroke-linejoin'] === 'bevel' ? 'bevel' : 'miter',
                    miterLimit: Math.max(1, parseFloat(st['stroke-miterlimit'] ?? '4') || 4),
                };
                for (const sub of boxSubs) add(strokeSpec.color, strokeA, strokeSub(sub, style), IDENTITY);
            }
        }
    }
    const out = [...pieces.values()].filter(p => p.indices.length);
    if (!out.length) return null;
    // Winding: the mesh is y-up and faces +z, so counter-clockwise from the front. The flip above reversed box-space triangles.
    for (const p of out) for (let i = 0; i + 2 < p.indices.length; i += 3) {
        const a = p.indices[i] * 3; const b = p.indices[i + 1] * 3; const c = p.indices[i + 2] * 3;
        const cr = (p.positions[b] - p.positions[a]) * (p.positions[c + 1] - p.positions[a + 1]) - (p.positions[b + 1] - p.positions[a + 1]) * (p.positions[c] - p.positions[a]);
        if (cr < 0) { const t = p.indices[i + 1]; p.indices[i + 1] = p.indices[i + 2]; p.indices[i + 2] = t; }
    }
    return { width: W, height: H, pieces: out };
}
